import { Chip, useDisclosure } from '@ds';
import TabSection from '@features/components/CaseOverview/components/TabSection/TabSection';
import SectionCard from '@features/components/CaseOverview/components/SectionCard/SectionCard';
import { CaseAttachmentsButton } from '@features/components/CaseOverview/components/CaseAttachmentsButton/CaseAttachmentsButton';
import { EtaSections } from '@features/components/CaseOverview/components/Eta/EtaSections';
import SectionRow from '@features/components/CaseOverview/components/SectionRow/SectionRow';
import { getClinicalRecordByType, yesNo } from '@/utils/helpers';
import { Case, CaseOrigin, ClinicalItemType } from '@/api/apis/case';
import { EtaType } from '@/types';
import { EditDonorCaseModal } from '../components/EditCaseForms/EditCaseDonor/EditCaseDonorModal';
import { WolsLocationRow } from '../components/WolsLocationRow';
import { OpoClinicalRequestRow } from '../components/OpoClinicalRequestRow';
import { FacilityRows } from '../components/FacilityRows';
import { useDonorPermissions } from '../hooks/useDonorPermissions';
import { useDonorEta } from '../hooks/useDonorEta';
import { OpoSection } from './OpoSection';

type DonorProps = {
  caseData: Case;
};

export const Donor = ({ caseData }: DonorProps) => {
  const { isOpen: isOpenDonor, onOpen: onOpenDonor, onClose: onCloseDonor } = useDisclosure();

  const {
    hasEditPermission,
    disableEditing,
    hasAttachmentsReadPermission,
    hasAttachmentsCreatePermission,
    hasAttachmentsUpdatePermission,
    hasAttachmentsDeletePermission,
    isAttachmentsDisabled,
  } = useDonorPermissions(caseData);

  const { donorORData, formatedDelay, delayInfo } = useDonorEta(caseData, hasEditPermission);

  const { caseId, assignedOpoContacts, opoDetails, donorHospitalDetails, pediatricCase, files, links, clinicalData } =
    caseData;

  const wolsRecord = getClinicalRecordByType(clinicalData, ClinicalItemType.WithdrawalOfLifeSupport);

  const isOpoSide = caseData.creationSide === CaseOrigin.Opo;

  return (
    <TabSection onSectionEdit={disableEditing ? undefined : onOpenDonor} title="Retrieval Info">
      {isOpenDonor && <EditDonorCaseModal isOpen={isOpenDonor} onClose={onCloseDonor} caseData={caseData} />}

      <SectionCard>
        <FacilityRows facilityDetails={donorHospitalDetails} label="Donor Hospital" addressLabel="Donor Address" />

        <EtaSections
          displayEtaTypes={[EtaType.donorOR, EtaType.delay]}
          donorORData={donorORData}
          formatedDelay={formatedDelay}
          delayInfo={delayInfo}
          SectionRowComponent={SectionRow}
        />

        <OpoSection assignedOpoContacts={assignedOpoContacts} opoDetails={opoDetails} />

        <SectionRow label="Attachments & Links">
          <CaseAttachmentsButton
            files={files}
            links={links}
            caseId={caseId}
            disabled={!hasAttachmentsReadPermission || isAttachmentsDisabled}
            showUploadBtn={hasAttachmentsCreatePermission && !isAttachmentsDisabled}
            showDownloadBtn={hasAttachmentsReadPermission}
            showDeleteBtn={hasAttachmentsDeletePermission && !isAttachmentsDisabled}
            showEditBtn={hasAttachmentsUpdatePermission && !isAttachmentsDisabled}
            hintLabel="Uploaded by OPO"
            showRedactionInstructions
          />
        </SectionRow>

        <SectionRow label="Under 40kg">
          <Chip size="sm">{yesNo(pediatricCase)}</Chip>
        </SectionRow>

        <WolsLocationRow wolsRecord={wolsRecord} />
        {isOpoSide && (
          <OpoClinicalRequestRow
            caseId={caseId}
            organType={caseData.organType}
            donorType={caseData.donorType}
            status={caseData.status}
            matchId={caseData.matchId}
            donorData={caseData.donorData}
          />
        )}
      </SectionCard>
    </TabSection>
  );
};
